import React, { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "../services/supabase";
import { loadGoogleMaps } from "../services/loadGoogleMaps";

export default function ZonesPage() {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const drawingManagerRef = useRef(null);
  const polygonRef = useRef(null);

  const [polygonCoords, setPolygonCoords] = useState([]);
  const [zoneName, setZoneName] = useState("");
  const [zones, setZones] = useState([]);

  // -----------------------------------
  // UPDATE POLYGON COORDS
  // -----------------------------------
  const updatePolygon = useCallback(() => {
    const polygon = polygonRef.current;
    if (!polygon) return;

    const path = polygon.getPath().getArray();
    setPolygonCoords(path.map((p) => ({ lat: p.lat(), lng: p.lng() })));
  }, []);

  // -----------------------------------
  // INIT DRAWING MANAGER
  // -----------------------------------
  const initDrawingManager = useCallback(
    (map) => {
      const drawingManager = new window.google.maps.drawing.DrawingManager({
        drawingMode: window.google.maps.drawing.OverlayType.POLYGON,
        drawingControl: true,
        drawingControlOptions: {
          position: window.google.maps.ControlPosition.TOP_CENTER,
          drawingModes: ["polygon"],
        },
        polygonOptions: {
          fillColor: "#4da3ff",
          fillOpacity: 0.3,
          strokeWeight: 2,
          clickable: true,
          editable: true,
          strokeColor: "#004aad",
        },
      });

      drawingManager.setMap(map);
      drawingManagerRef.current = drawingManager;

      window.google.maps.event.addListener(
        drawingManager,
        "polygoncomplete",
        (polygon) => {
          if (polygonRef.current) polygonRef.current.setMap(null);

          polygonRef.current = polygon;

          updatePolygon();
          polygon.getPath().addListener("set_at", updatePolygon);
          polygon.getPath().addListener("insert_at", updatePolygon);
        }
      );
    },
    [updatePolygon]
  );

  // -----------------------------------
  // INIT MAP
  // -----------------------------------
  const initMap = useCallback(() => {
    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: 45.5017, lng: -73.5673 },
      zoom: 11,
    });

    mapInstanceRef.current = map;
    initDrawingManager(map);
  }, [initDrawingManager]);

  // -----------------------------------
  // AFFICHER ZONES EXISTANTES
  // -----------------------------------
  const drawExistingZones = useCallback((zonesList) => {
    if (!window.google || !mapInstanceRef.current) return;

    zonesList.forEach((zone) => {
      if (!zone.coords || zone.coords.length < 3) return;

      new window.google.maps.Polygon({
        paths: zone.coords,
        map: mapInstanceRef.current,
        strokeColor: "#e53935",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#e53935",
        fillOpacity: 0.25,
      });
    });
  }, []);

  // -----------------------------------
  // LOAD ZONES
  // -----------------------------------
  const loadZones = useCallback(async () => {
    const { data, error } = await supabase.rpc("get_zones_geojson");

    if (error) {
      console.error("Erreur load zones:", error);
      return;
    }

    const formatted = data.map((z) => {
      const geo = JSON.parse(z.geojson);

      const coords = geo.coordinates[0][0].map(([lng, lat]) => ({
        lat,
        lng,
      }));

      return {
        id: z.id,
        name: z.name,
        coords,
      };
    });

    setZones(formatted);
    drawExistingZones(formatted);
  }, [drawExistingZones]);

  // -----------------------------------
  // INIT MAP + LOAD ZONES
  // -----------------------------------
  useEffect(() => {
    const init = async () => {
      await loadGoogleMaps(process.env.REACT_APP_GOOGLE_MAPS_API_KEY);
      initMap();
      loadZones();
    };

    init();
  }, [initMap, loadZones]);

  // -----------------------------------
  // CONVERT TO WKT
  // -----------------------------------
  const convertToWKT = () => {
    if (polygonCoords.length < 3) return null;

    const closed = [...polygonCoords, polygonCoords[0]];
    const coordText = closed.map((p) => `${p.lng} ${p.lat}`).join(", ");

    return `MULTIPOLYGON(((${coordText})))`;
  };

  // -----------------------------------
  // SAVE ZONE
  // -----------------------------------
  const saveZone = async () => {
    if (!zoneName.trim()) return alert("Veuillez nommer la zone.");

    const wkt = convertToWKT();
    if (!wkt) return alert("Veuillez dessiner un polygone.");

    const { error } = await supabase.rpc("insert_zone_from_wkt", {
      zone_name: zoneName,
      wkt_value: wkt,
    });

    if (error) {
      console.error(error);
      alert("Erreur lors de l'enregistrement.");
      return;
    }

    alert("Zone enregistrée !");
    setZoneName("");
    setPolygonCoords([]);

    if (polygonRef.current) polygonRef.current.setMap(null);
    loadZones();
  };

  // -----------------------------------
  // DELETE ZONE
  // -----------------------------------
  const deleteZone = async (zoneId) => {
    if (!window.confirm("Supprimer cette zone ?")) return;

    const { error } = await supabase.from("zones").delete().eq("id", zoneId);
    if (error) return alert("Erreur suppression.");

    loadZones();
  };

  return (
    <div>
      <h2>Zones</h2>

      <div className="zone-controls">
        <input
          type="text"
          placeholder="Nom de la zone"
          value={zoneName}
          onChange={(e) => setZoneName(e.target.value)}
        />
        <button className="btn-primary" onClick={saveZone}>
          Enregistrer
        </button>
      </div>

      <div ref={mapRef} className="zone-map"></div>

      <h3>Zones existantes</h3>
      <ul className="zone-list">
        {zones.map((z) => (
          <li key={z.id}>
            <strong>{z.name}</strong>
            <button className="btn-danger" onClick={() => deleteZone(z.id)}>
              Supprimer
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}



